/**
 * Módulo Vault de origen (columna «Módulo»): nombre del menú lateral donde ocurre la acción.
 * Varios `entity_type` del API pueden mapear al mismo módulo (p. ej. ingest_project → Carga de documentos).
 */
const AUDIT_MODULE_LABELS: Record<string, string> = {
  legal_hold: 'Custodia legal',
  retention_policy: 'Retención documental',
  retention: 'Retención documental',
  user: 'Gestión de usuarios',
  ingest: 'Carga de documentos',
  ingest_project: 'Carga de documentos',
  ingest_subfolder: 'Carga de documentos',
  document: 'Bóveda digital',
  notification: 'Notificaciones',
  audit: 'Auditoría',
  repository: 'Bóveda digital',
  repository_admin: 'Bóveda digital',
  cloud: 'Respaldo externo',
  preservation: 'Preservación digital',
};

/** Etiqueta del filtro «Módulo» (más específica cuando hay subtipos de ingesta). */
const AUDIT_ENTITY_TYPE_FILTER_LABELS: Record<string, string> = {
  legal_hold: 'Custodia legal',
  retention_policy: 'Retención documental',
  retention: 'Retención documental',
  user: 'Gestión de usuarios',
  ingest: 'Carga de documentos — ingesta de archivos',
  ingest_project: 'Carga de documentos — expediente',
  ingest_subfolder: 'Carga de documentos — subcarpeta',
  document: 'Bóveda digital',
  notification: 'Notificaciones',
  audit: 'Auditoría',
  repository: 'Bóveda digital',
  repository_admin: 'Bóveda digital',
  cloud: 'Respaldo externo',
  preservation: 'Preservación digital',
};

function humanizeAuditToken(raw: string): string {
  return raw
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Módulo Vault de origen (badge en columna «Módulo»). */
export function auditModuleLabel(entityType: string | null | undefined): string {
  const raw = (entityType ?? '').trim();
  if (!raw) return '—';
  const mapped = AUDIT_MODULE_LABELS[raw.toLowerCase()];
  if (mapped) return mapped;
  return humanizeAuditToken(raw);
}

/** Etiqueta para el filtro desplegable «Módulo» (incluye subtipo cuando aplica). */
export function auditEntityTypeFilterLabel(entityType: string | null | undefined): string {
  const raw = (entityType ?? '').trim();
  if (!raw) return '—';
  const mapped = AUDIT_ENTITY_TYPE_FILTER_LABELS[raw.toLowerCase()];
  if (mapped) return mapped;
  return humanizeAuditToken(raw);
}

/** @deprecated Usar auditModuleLabel (columna) o auditEntityTypeFilterLabel (filtro). */
export function auditEntityTypeLabel(entityType: string | null | undefined): string {
  return auditModuleLabel(entityType);
}

export function auditActionLabel(action: string | null | undefined): string {
  const actionUpper = (action ?? '').toUpperCase();
  if (actionUpper.includes('CREATE') || actionUpper.includes('CREAR')) return 'Crear';
  if (actionUpper.includes('UPDATE') || actionUpper.includes('ACTUALIZAR')) return 'Actualizar';
  if (actionUpper.includes('DELETE') || actionUpper.includes('ELIMINAR')) return 'Eliminar';
  if (actionUpper.includes('UPLOAD') || actionUpper.includes('SUBIR')) return 'Subir archivos';
  if (actionUpper.includes('LOGIN')) return 'Inicio de sesión';
  if (actionUpper.includes('LOGOUT')) return 'Cierre de sesión';
  return (action ?? '').trim() || '—';
}

/** Acción contextual según módulo/entidad (p. ej. CREATE + ingest_subfolder → «Crear subcarpeta»). */
const INGEST_ACTION_LABELS: Record<string, Partial<Record<string, string>>> = {
  ingest_project: {
    CREATE: 'Crear expediente',
    UPDATE: 'Actualizar expediente',
    DELETE: 'Desactivar expediente',
  },
  ingest_subfolder: {
    CREATE: 'Crear subcarpeta',
    UPDATE: 'Actualizar subcarpeta',
    DELETE: 'Eliminar subcarpeta',
  },
  ingest: {
    UPLOAD: 'Subir archivos',
    CREATE: 'Subir archivos',
  },
};

function resolveIngestActionLabel(actionUpper: string, entityType: string): string | null {
  const hints = INGEST_ACTION_LABELS[entityType];
  if (!hints) return null;
  for (const [token, label] of Object.entries(hints)) {
    if (label && actionUpper.includes(token)) return label;
  }
  return null;
}

export function auditActionDisplayLabel(
  action: string | null | undefined,
  entityType?: string | null,
): string {
  const actionUpper = (action ?? '').toUpperCase();
  const entity = (entityType ?? '').trim().toLowerCase();
  if (entity) {
    const contextual = resolveIngestActionLabel(actionUpper, entity);
    if (contextual) return contextual;
  }
  return auditActionLabel(action);
}

/** ID visible cuando `entity_id` viene null (p. ej. ingesta: project_id / upload_id en details). */
export function auditResourceId(
  entityId: string | null | undefined,
  details?: Record<string, unknown> | null,
): string {
  const direct = (entityId ?? '').trim();
  if (direct) return direct;
  if (!details) return '';
  for (const key of ['project_id', 'upload_id', 'subfolder_id', 'entity_id']) {
    const value = details[key];
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
}
